/**
 * Respuestas de la auditoría del 11/10/2026 sobre la configuración exportada el 08/10/2026.
 *
 * Una sola fuente para el informe de pendientes (data/guide/technicalAudit), las recetas, las misiones y los eventos.
 * - «known»: solo lo que aparece en la exportación del 08/10/2026 (confirmado por configuración, nunca «probado en el juego»).
 * - «missing»: únicamente el campo que falta; se resolverá con la fuente indicada.
 * - No confirma que la configuración del 08/10 siga desplegada hoy ni autoriza cambios en el GameServer.
 * - Los conteos de la auditoría son referencias: el panel calcula los suyos desde los CSV.
 */
export const SOURCE_AUDIT_LABEL = 'Auditoría del paquete · 11/10/2026 · configuración del 08/10/2026'

export type AuditFact = {
  known: string[]
  missing: string[]
  /** Aclaración de lectura (qué NO demuestra el dato). */
  note?: string
  sources: string[]
}

// ── Recetas ──────────────────────────────────────────────────────────────────
/** Bloque común: lo que falta en cada una de las 18 recetas (fuente: ChaosMix / ChaosBox del servidor). */
export const RECIPE_COMMON_MISSING = [
  'Relación real entre ingredientes y variantes',
  'Cantidades',
  'Mínimos de nivel u opción',
  'Zen',
  'Fórmula de éxito por cuenta',
  'Efectos del fallo',
  'Mecanismo y condiciones',
  'Resultados posibles',
]

const PROFILE_NOTE = 'Los perfiles de 25_option_profiles describen atributos posibles del resultado, no la probabilidad de fabricación; falta el enlace desde la implementación.'
const TRAINER_NOTE = 'NPC canónico: Trainer (226); Pet Trainer (234) es su alias (administración, 11/10/2026). Esa identidad no confirma ingredientes ni tasa.'

/** Clave = la del informe sin el prefijo «recipe:» (variantes como «capas:dl»). */
export const recipeAudit: Record<string, AuditFact> = {
  'capes-third': {
    known: ['Cape of Emperor (12,40) → Dark Lord, evolución mínima 3 (05_item_classes)', 'Lord of the Cape (12,50) → Rage Fighter, evolución mínima 3 (05_item_classes)'],
    missing: RECIPE_COMMON_MISSING,
    sources: ['01_items', '05_item_classes'],
  },
  'cloak-of-warrior': {
    known: ['Cloak of Warrior (12,49) → Rage Fighter, evolución mínima 1 (05_item_classes)'],
    missing: RECIPE_COMMON_MISSING,
    note: 'No hay una receta de la Chaos Machine demostrada para esta capa.',
    sources: ['01_items', '05_item_classes'],
  },
  'jewel-packs': {
    known: [
      'Identificados 10 paquetes: Bless 12,30 · Soul 12,31 · Life 12,136 · Creation 12,137 · Guardian 12,138 · Gemstone 12,139 · Harmony 12,140 · Chaos 12,141 · Lower Refining Stone 12,142 · Higher Refining Stone 12,143',
      'Siete tienen rutas de recompensa de Balgass/Selupan (10_drop_routes)',
    ],
    missing: ['Cantidad de joyas de cada nivel o variante', 'NPC y mecanismo para empaquetar y desempaquetar', 'Costes, requisitos y habilitación vigente'],
    note: 'Una ruta de recompensa no demuestra que se puedan empaquetar ni que contengan 10, 20 o 30 unidades.',
    sources: ['01_items', '10_drop_routes'],
  },
  'arma-chaos': {
    known: ['Chaos Dragon Axe 2,6 · Chaos Nature Bow 4,6 · Chaos Lighting Staff 5,7 · Jewel of Chaos 12,15', 'Perfiles comentados para mezclas: 0:3, 1:3, 2:3, 3:3'],
    missing: RECIPE_COMMON_MISSING,
    note: `Las rutas de cajas o recompensas de las armas Chaos son otra vía de obtención, no prueba de la receta. ${PROFILE_NOTE}`,
    sources: ['01_items', '25_option_profiles'],
  },
  'alas-evo-1': {
    known: ['Armas Chaos 2,6 · 4,6 · 5,7 · Jewel of Chaos 12,15', 'Wings of Elf 12,0 · Wings of Heaven 12,1 · Wings of Satan 12,2 · Wing of Curse 12,41', 'Perfiles comentados para mezclas: 2:5, 3:5'],
    missing: RECIPE_COMMON_MISSING,
    note: PROFILE_NOTE,
    sources: ['01_items', '25_option_profiles'],
  },
  'alas-evo-2': {
    known: ["Jewel of Chaos 12,15 · Loch's Feather 13,14@0", 'Perfiles comentados para mezclas: 2:4, 3:4, 4:4, 4:5'],
    missing: RECIPE_COMMON_MISSING,
    note: `Loch's Feather (13,14@0) comparte ID base con Crest of Monarch (13,14@1); las variantes no son intercambiables. ${PROFILE_NOTE}`,
    sources: ['01_items', '02_item_variants', '25_option_profiles'],
  },
  'capas:dl': {
    known: ["Jewel of Chaos 12,15 · Loch's Feather 13,14 · Cape of Lord 13,30", 'Crest of Monarch es 13,14@1 (no 13,32)'],
    missing: RECIPE_COMMON_MISSING,
    sources: ['01_items', '02_item_variants'],
  },
  'pluma-condor': {
    known: ['Jewel of Chaos 12,15 · Bundled Jewel of Soul 12,31 · Condor Feather 13,53 · Jewel of Creation 14,22', 'Condor Feather tiene además una vía documentada como premio de Balgass'],
    missing: RECIPE_COMMON_MISSING,
    note: 'La vía de Balgass no confirma la receta; la cantidad del paquete de Soul no está documentada.',
    sources: ['01_items', '10_drop_routes'],
  },
  'alas-evo-3': {
    known: [
      'Jewel of Chaos 12,15 · Bundled Bless 12,30 · Bundled Soul 12,31 · Jewel of Creation 14,22 · Condor Flame 13,52 · Condor Feather 13,53',
      'Resultados identificados: 12,36 · 12,37 · 12,38 · 12,39 · 12,40 · 12,42 · 12,43 · 12,50',
      'Perfiles comentados para mezclas: 2:6, 3:6, 4:6',
    ],
    missing: RECIPE_COMMON_MISSING,
    note: `«10 joyas por paquete» sigue pendiente de una fuente de empaquetado. ${PROFILE_NOTE}`,
    sources: ['01_items', '25_option_profiles'],
  },
  'dark-horse': {
    known: ['Jewel of Chaos 12,15 · Dark Horse 13,4 · Spirit of Dark Horse 13,31@0 · Bless 14,13 · Soul 14,14 · Creation 14,22', 'El espíritu tiene drop especial rule:19 y objetivos'],
    missing: RECIPE_COMMON_MISSING,
    note: `El drop del espíritu no confirma la cantidad de joyas ni la combinación. ${TRAINER_NOTE}`,
    sources: ['01_items', '02_item_variants', '10_drop_routes', '12_item_drop_rules'],
  },
  'dark-raven': {
    known: ['Jewel of Chaos 12,15 · Dark Raven 13,5 · Spirit of Dark Raven 13,31@1 · Bless 14,13 · Soul 14,14 · Creation 14,22', 'El espíritu tiene drop especial rule:20 y objetivos'],
    missing: RECIPE_COMMON_MISSING,
    note: `El drop del espíritu no confirma la cantidad de joyas ni la combinación. ${TRAINER_NOTE}`,
    sources: ['01_items', '02_item_variants', '10_drop_routes', '12_item_drop_rules'],
  },
  dinorant: {
    known: ['Jewel of Chaos 12,15 · Horn of Uniria 13,2 · Horn of Dinorant 13,3', 'Perfil comentado para mezclas: 4:3'],
    missing: RECIPE_COMMON_MISSING,
    note: `Las 10 Uniria de la referencia clásica no se confirman en los CSV. ${PROFILE_NOTE}`,
    sources: ['01_items', '25_option_profiles'],
  },
  'fenrir-fragmento': {
    known: ['Jewel of Chaos 12,15 · Splinter of Armor 13,32 · Bless of Guardian 13,33 · Fragment of Horn 13,35'],
    missing: RECIPE_COMMON_MISSING,
    note: 'No se confirman las cantidades 20/20 ni la mezcla.',
    sources: ['01_items'],
  },
  'fenrir-cuerno-roto': {
    known: ['Jewel of Chaos 12,15 · Claw of Beast 13,34 · Fragment of Horn 13,35 · Broken Horn 13,36'],
    missing: RECIPE_COMMON_MISSING,
    note: 'No se confirman las cantidades 5/1 ni la mezcla.',
    sources: ['01_items'],
  },
  fenrir: {
    known: ['Jewel of Chaos 12,15 · Broken Horn 13,36 · Horn of Fenrir 13,37 · Jewel of Life 14,16', 'Horn of Fenrir tiene además una vía documentada como premio de Illusion Temple (35_event_direct_items)'],
    missing: RECIPE_COMMON_MISSING,
    note: 'El premio de Illusion Temple no confirma la mezcla de Broken Horn con tres Jewel of Life.',
    sources: ['01_items', '35_event_direct_items'],
  },
  'entrada-devil-square': {
    known: ["Jewel of Chaos 12,15 · Devil's Eye 14,17 · Devil's Key 14,18 · Devil's Invitation 14,19", 'Variantes +1 a +7 documentadas de los tres objetos'],
    missing: RECIPE_COMMON_MISSING,
    note: 'Identifica los objetos; no prueba la receta ni que sus niveles deban coincidir.',
    sources: ['01_items', '02_item_variants'],
  },
  'entrada-blood-castle': {
    known: ['Jewel of Chaos 12,15 · Scroll of Archangel 13,16 · Blood Bone 13,17 · Invisibility Cloak 13,18', 'Variantes +1 a +8 documentadas de los tres objetos'],
    missing: RECIPE_COMMON_MISSING,
    note: 'Identifica los objetos; no prueba la receta ni que sus niveles deban coincidir.',
    sources: ['01_items', '02_item_variants'],
  },
  'entrada-illusion-temple': {
    known: ['Jewel of Chaos 12,15 · Old Scroll 13,49 · Illusion Sorcerer Covenant 13,50 · Scroll of Blood 13,51'],
    missing: RECIPE_COMMON_MISSING,
    note: 'Los nombres no prueban la receta, las cantidades ni los mínimos.',
    sources: ['01_items'],
  },
}

/** Auditoría de una receta por su clave (p. ej. «dark-horse» o «capas:dl»). */
export const recipeAuditOf = (key: string) => recipeAudit[key]

// ── Misiones ─────────────────────────────────────────────────────────────────
export type QuestAuditKey = 'sebina' | 'marlon' | 'combo' | 'devin'

export const questAudit: Record<QuestAuditKey, AuditFact & { questIds: number[] }> = {
  sebina: {
    questIds: [0, 1],
    known: ['Requisitos y objetivos exportados (nivel 150, NPC 235; la misión 1 exige la 0 terminada)'],
    missing: ['Cambio de clase al completar cada etapa, según la clase de origen', 'Puntos, otros premios y efectos'],
    sources: ['37_quest_rules', '39_quest_objectives', '40_quest_objective_classes'],
  },
  marlon: {
    questIds: [2],
    known: ['Requisitos exportados (nivel 220, NPC 229, misión 1 terminada)'],
    missing: ['Beneficio al completar', 'Puntos adicionales por nivel, retroactividad y nivel desde el que se aplican'],
    note: 'El nivel 220 es el requisito para aceptar la misión; no demuestra desde qué nivel se aplica un beneficio.',
    sources: ['37_quest_rules', '39_quest_objectives'],
  },
  combo: {
    questIds: [3],
    known: ['Requisitos exportados (nivel 220, Marlon 229, misión 2 terminada)', 'quest-rule-015 solo habilita a Dark Knight con required_change_up_plus_one = 2 (mínimo, no igualdad)'],
    missing: ['IDs y orden de las habilidades válidas', 'Ventana de tiempo', 'Requisitos y restricciones de ejecución', 'Si el desbloqueo se conserva al evolucionar'],
    note: 'La elegibilidad de la misión no demuestra la secuencia del combo ni lo oculta a una evolución posterior.',
    sources: ['37_quest_rules', '38_quest_rule_classes', '39_quest_objectives'],
  },
  devin: {
    questIds: [4, 5, 6],
    known: ['Objetivos y orden exportados (misión 4: nivel 380; misiones 5 y 6: nivel 400)'],
    missing: ['Efectos y recompensas de cada etapa', 'En qué etapa cambia la clase, según la clase de origen'],
    sources: ['37_quest_rules', '39_quest_objectives'],
  },
}

export const questAuditFor = (questId: number) => (Object.entries(questAudit) as [QuestAuditKey, (typeof questAudit)[QuestAuditKey]][]).find(([, q]) => q.questIds.includes(questId))

// ── Eventos ──────────────────────────────────────────────────────────────────
const TRIGGER_MISSING = ['Requisitos de acceso', 'Mecanismo de entrada (NPC, ítem o misión)', 'Condición de activación', 'Tiempo de reapertura']
const TRIGGER_NOTE = 'Evento por activación: sin frecuencia fija. La duración de la partida no es el tiempo de reapertura, y los tiers por nivel del grupo determinan recompensas, no requisitos de acceso.'

export const eventAudit: Record<string, AuditFact> = {
  'event:double_goer': {
    known: ['Activo en servermain1 y servermain2 (no en servercastle)', 'Duración configurada: 600 segundos', 'Un mapa aleatorio por entrada entre 65 y 68'],
    missing: TRIGGER_MISSING,
    note: TRIGGER_NOTE,
    sources: ['27_events', '30_event_map_choices', 'Fuente nativa: Event/DoubleGoer.dat'],
  },
  'event:imperial_guardian': {
    known: ['Activo en servermain1 y servermain2 (no en servercastle)', 'Mapa por día: lunes y jueves 69 · martes y viernes 70 · miércoles y sábado 71 · domingo 72'],
    missing: TRIGGER_MISSING,
    note: TRIGGER_NOTE,
    sources: ['27_events', '30_event_map_choices', 'Fuente nativa: Event/ImperialGuardian.dat'],
  },
  'event:kanturu': {
    known: ['Activo en servermain1 y servermain2 (no en servercastle)', 'Mapa fijo 39; monstruos y fases documentados'],
    missing: [...TRIGGER_MISSING, 'Posiciones o reglas de aparición por fase'],
    note: TRIGGER_NOTE,
    sources: ['27_events', '31_event_monsters', 'Fuente nativa: Event/Kanturu.dat'],
  },
  'event:raklion': {
    known: ['Activo en servermain1 y servermain2 (no en servercastle)', 'Mapa fijo 58', 'Entidades: Selupan 459 y Spider Eggs 460, 461 y 462'],
    missing: [...TRIGGER_MISSING, 'Posiciones o reglas de aparición por fase'],
    note: TRIGGER_NOTE,
    sources: ['27_events', '31_event_monsters', 'Fuente nativa: Event/Raklion.dat'],
  },
  'invasion:7': {
    known: ['Inactivo en la configuración del 08/10/2026 (active_configured = false)', 'Sin calendario automático'],
    missing: ['Si el staff lo activa manualmente o si cambió su estado'],
    note: 'Que el sistema global de invasiones esté habilitado no activa este evento.',
    sources: ['27_events'],
  },
}

// ── Sistemas: cada uno con su verificación ───────────────────────────────────
export type SystemAudit = { name: string; known: string; missing: string; question: string }

export const systemAudit: Record<string, SystemAudit> = {
  clases: {
    name: 'Clases y habilidades',
    known: '04_classes marca 7 clases activas (DW, DK, ELF, MG, DL, SUM, RF) y 8 inactivas. 01_items aporta skill_id, required_level_config y required_energy_config; 05_item_classes aporta clase y evolución del ítem (p. ej. Scroll of Evil Spirit 15,8: skill 9, energía 100). drop_level no es el nivel de aprendizaje.',
    missing: 'Estadísticas iniciales por clase · puntos por nivel o reset · catálogo y condiciones completas de aprendizaje y uso de habilidades',
    question: 'Enviar GameServerInfo - Character.dat y Skill.dat vigentes (main1, main2, castle).',
  },
  mejoras: {
    name: 'Mejoras de ítems',
    known: '25_option_profiles contiene distribuciones de opciones de drops y bolsas; no son tasas de mejora ni de fallo.',
    missing: 'Éxito por mejora (+10 a +15) y por cuenta · efectos del fallo · aplicación real de Luck y opción adicional al mejorar',
    question: 'Enviar la configuración de mejoras (ChaosMix e ItemOptionRate) vigente.',
  },
  'sistemas-avanzados': {
    name: 'Socket, Harmony y opción 380',
    known: '07_maps marca socket_items_allowed = True en Raklion 1 (57) y Raklion 2 (58). Jewel of Harmony, Gemstone y piedras de refinación están catalogadas. Ver sus ítems no demuestra que los tres sistemas estén activos.',
    missing: 'Habilitación global de Socket, Harmony y opción 380 · restricciones, recetas y efectos',
    question: 'Enviar SocketItemType.txt, 380ItemType.txt y la configuración de Harmony.',
  },
  master: {
    name: 'Árbol Master',
    known: 'master_character_required de 33_event_rooms es un requisito de sala, no del árbol.',
    missing: 'Nivel, reset o evolución necesarios · puntos y progresión · habilidades, límites y reseteo del árbol',
    question: 'Enviar la configuración real del árbol Master o documentación oficial.',
  },
  helper: {
    name: 'MU Helper y OffHelper',
    known: 'Los 47 CSV no contienen configuración del Helper. Los bonus de 44–46 no lo documentan.',
    missing: 'Habilitación, acceso y controles · nivel o cuenta exigidos · coste y cobro · tiempo, límites, desconexión y reconexión · funciones y restricciones',
    question: 'Enviar la configuración real del MU Helper / OffHelper o documentación oficial.',
  },
  resets: {
    name: 'Resets y Grand Reset',
    known: 'reset_min/reset_max de 33_event_rooms son de acceso a eventos, no condiciones de reset.',
    missing: 'Nivel, coste y requisitos por reset y Grand Reset · qué se conserva · puntos, premios y límites',
    question: 'Enviar la configuración real de resets o documentación oficial.',
  },
  market: {
    name: 'Market, VIP y Pase de Batalla',
    known: '43_server_rates: AL0 Normal 25; AL1–AL3 PREMIUM 40 en main1, main2 y castle; monster_life_rate 100. item_drop_rate es un factor global de drop natural, no un porcentaje por ítem ni un precio. El mapa Loren Market no demuestra el Market web.',
    missing: 'Sistemas disponibles hoy · monedas, precios y comisiones · beneficios VIP · temporada, requisitos y recompensas del Pase · flujo y límites del Market',
    question: 'Enviar las reglas funcionales, precios, monedas y comisiones vigentes (sin código ni credenciales).',
  },
  comandos: {
    name: 'Comandos',
    known: 'Sin catálogo de comandos en los 47 CSV. No se publican comandos genéricos de MU como habilitados.',
    missing: 'Comando y alias exactos · función · habilitación · requisitos y costes · permisos por cuenta o rol',
    question: 'Enviar GameServerInfo - Command.dat vigente (main1, main2, castle).',
  },
  bonus: {
    name: 'Bonificaciones (BonusManager)',
    known: '44_bonus_definitions: bonus 0, 86400 s (24 h), has_schedule = False. 46_bonus_rules: bonus 0, tipo 0, AL0–AL3 [20,20,20,20], sin filtros. 45_bonus_schedules: bonus 1 y 2 a las 00:00:00 con fechas vacías; 1 y 2 no tienen definición en 44. La guía no anuncia ningún bonus activo.',
    missing: 'Definiciones y reglas de los bonus 1 y 2, o corrección de los horarios que los citan · significado de bonus_type = 0, valores y comodines de calendario · activación vigente',
    question: 'Enviar Event/BonusManager.dat vigente.',
  },
}

// ── Solicitudes de archivos nativos (se piden una sola vez) ──────────────────
export const nativeFileRequests = {
  npc: 'Monster/MonsterSetBase/ y MonsterSetBaseCS/ completos (con secciones de NPC), MapServerInfo.dat, KanturuMonsterSetBase.txt y MonsterAIGroup.txt. Exportar NPC por separado: ID fuente/canónico, mapa, X/Y, dirección, subservidor, sección y condiciones; y la rotación de Marlon si no está ahí.',
  shops: 'Archivos vigentes de tiendas y asignación NPC/instancia → tienda: shop_id o servicio, item_id, variante/nivel/opciones, cantidad, precio y moneda (o su regla). Nombres reales de archivo por identificar.',
  server: 'GameServerInfo - ChaosMix.dat (main1, main2, castle), Item/ItemOptionRate.txt y la lógica de ChaosBox.cpp (o tabla oficial de las 18 recetas y del empaquetado) · Quest/Quest.txt, QuestObjective.txt y la implementación de recompensas, cambio de clase, puntos y validación del combo · Event/*.dat de Blood Castle, Castle Deep, Chaos Castle, Devil Square, Double Goer, Illusion Temple, Imperial Guardian, Kanturu y Raklion.',
  invasions: 'EventItemBag 001 Skeleton King, 139 Death Bone, 002 Red Dragon, 027 White Wizard, 137/138 White Wizard Soldier/Archer, EventItemBagManager.txt y Event/InvasionManager.dat.',
  systems: 'GameServerInfo - Character.dat, Skill.dat, Command.dat y Custom.dat (main1, main2, castle) · Event/BonusManager.dat · Item/SocketItemType.txt y 380ItemType.txt · configuración real de Helper/OffHelper, Master, resets, Harmony, Market, VIP y Pase.',
}
